import { clientLogger } from '@/lib/clientLogger';
import { useEffect, useRef, useState } from 'react';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { AlertCircle, Move } from 'lucide-react';
import { ENV } from '@/config/environment';
import { loadGoogleMaps, isGoogleMapsReady } from '@/lib/loadGoogleMaps';
import { getCachedGeocode, setCachedGeocode } from '@/lib/geocodeCache';

interface MapWidgetProps {
  address: string | null | undefined;
  height?: string;
}

// Static-by-default: every render is a single Maps Static API image (server-side
// geocoded, cheap, no Dynamic Maps load, no Geocoding request). The live,
// pannable map is loaded only on first click — so Maps-JS/Geocoding are billed
// on the rare interaction, not on every job-details open. See geocodeCache.ts.

/**
 * Builds a Maps Static API URL. The Static API geocodes the address server-side
 * (no separate Geocoding call), so this is the cheapest way to show an address.
 *
 * TODO(maps-language): add `&language=en` — labels currently follow the caller's
 * Accept-Language. Same fix needed in loadGoogleMaps.ts.
 */
function staticMapUrl(address: string): string {
  const q = encodeURIComponent(address);
  return (
    `https://maps.googleapis.com/maps/api/staticmap?center=${q}&zoom=14` +
    `&size=600x400&scale=2&markers=color:red%7C${q}&key=${ENV.GOOGLE_MAPS.API_KEY}`
  );
}

export function MapWidget({ address, height = '400px' }: MapWidgetProps) {
  const trimmed = address?.trim() || '';

  // Debounce the address used for the static image so the live new-job form
  // doesn't request a new image on every keystroke.
  const [debouncedAddress, setDebouncedAddress] = useState(trimmed);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedAddress(trimmed), 500);
    return () => clearTimeout(id);
  }, [trimmed]);

  const [interactive, setInteractive] = useState(false);

  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load + render the live map once the user opts into interaction.
  useEffect(() => {
    if (!interactive || !debouncedAddress || !ENV.GOOGLE_MAPS.API_KEY) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        await loadGoogleMaps();
        if (cancelled || !mapRef.current || !isGoogleMapsReady()) return;

        // Cache-first: only geocode on a miss (autocomplete pre-seeds the cache).
        let location = getCachedGeocode(debouncedAddress);
        if (location === null) throw new Error('ZERO_RESULTS (cached)');
        if (!location) {
          const geocoder = new google.maps.Geocoder();
          const result = await geocoder.geocode({ address: debouncedAddress });
          const first = result.results?.[0];
          if (!first) throw new Error('ZERO_RESULTS');
          location = { lat: first.geometry.location.lat(), lng: first.geometry.location.lng() };
          setCachedGeocode(debouncedAddress, location);
        }
        if (cancelled || !mapRef.current) return;

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new google.maps.Map(mapRef.current, {
            center: location,
            zoom: 14,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            mapId: 'DEMO_MAP_ID', // required for AdvancedMarkerElement
          });
        } else {
          mapInstanceRef.current.setCenter(location);
        }

        if (!window.google?.maps?.marker?.AdvancedMarkerElement) {
          await google.maps.importLibrary('marker');
        }
        if (cancelled) return;
        if (!markerRef.current) {
          markerRef.current = new google.maps.marker.AdvancedMarkerElement({
            map: mapInstanceRef.current,
            position: location,
          });
        } else {
          markerRef.current.position = location;
        }
      } catch (err) {
        if (!cancelled) {
          clientLogger.error('Map failed to load:', err);
          setError('Unable to load the interactive map for this address.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [interactive, debouncedAddress]);

  if (!trimmed) return null;

  return (
    <div
      className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden relative"
      style={{ height }}
    >
      {/* Static image — always rendered; acts as placeholder under the live map. */}
      <img
        src={staticMapUrl(debouncedAddress)}
        alt={`Map of ${debouncedAddress}`}
        className="w-full h-full object-cover"
        loading="lazy"
      />

      {!interactive && (
        <button
          type="button"
          onClick={() => setInteractive(true)}
          className="absolute inset-0 flex items-end justify-center bg-transparent hover:bg-black/5 transition-colors"
          title="Click to move/zoom"
        >
          <span className="mb-3 flex items-center gap-1.5 text-xs font-medium text-gray-700 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-md px-2 py-1 shadow-sm">
            <Move className="h-3.5 w-3.5" />
            Click to move/zoom
          </span>
        </button>
      )}

      {interactive && (
        <>
          <div ref={mapRef} className="absolute inset-0 w-full h-full" />
          {isLoading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
              <LoadingSpinner />
            </div>
          )}
          {error && (
            <div className="absolute inset-0 bg-white/95 flex items-center justify-center z-10 p-4">
              <div className="text-center">
                <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
